/**
 * PortalCategoriesSettings - Service categories management for Customer Portal
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Layers, ArrowLeft, Save, Loader2, Plus, Trash2, GripVertical, Tag, ChevronUp, ChevronDown, Pencil, Check, X, Download, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PortalCategoriesSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editName, setEditName] = useState('');
  const importRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/portal/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      // Sortiere nach order-Feld
      const sortedCategories = (settingsRes.data.service_categories || [])
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(sortedCategories);
      setServices(servicesRes.data || []);
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/portal/settings/categories`,
        { name: newCategoryName.trim(), order: categories.length },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories([...categories, response.data]);
      setNewCategoryName('');
      toast.success('Kategorie erstellt');
    } catch (error) {
      toast.error('Erstellen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/portal/settings/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(categories.filter(c => c.id !== categoryId));
      toast.success('Kategorie gelöscht');
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handleStartEdit = (category) => {
    setEditingCategory(category.id);
    setEditName(category.name);
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditName('');
  };

  const handleSaveEdit = async (categoryId) => {
    if (!editName.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/portal/settings/categories/${categoryId}`,
        { name: editName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(categories.map(c => 
        c.id === categoryId ? { ...c, name: editName.trim() } : c
      ));
      setEditingCategory(null);
      setEditName('');
      toast.success('Name aktualisiert');
    } catch (error) {
      toast.error('Aktualisieren fehlgeschlagen');
    }
  };

  const handleMoveCategory = async (categoryId, direction) => {
    const currentIndex = categories.findIndex(c => c.id === categoryId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    // Tausche Kategorien
    const newCategories = [...categories];
    [newCategories[currentIndex], newCategories[newIndex]] = [newCategories[newIndex], newCategories[currentIndex]];
    
    // Aktualisiere order-Werte
    const updatedCategories = newCategories.map((cat, idx) => ({ ...cat, order: idx }));
    setCategories(updatedCategories);
    
    // Speichere auf Server
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/portal/settings/categories/reorder`,
        { category_ids: updatedCategories.map(c => c.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reihenfolge aktualisiert');
    } catch (error) {
      // Bei Fehler zurücksetzen
      loadData();
      toast.error('Reihenfolge konnte nicht gespeichert werden');
    }
  };

  const handleToggleService = async (categoryId, serviceId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    
    const currentServices = category.service_ids || [];
    const newServices = currentServices.includes(serviceId)
      ? currentServices.filter(id => id !== serviceId)
      : [...currentServices, serviceId];
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/portal/settings/categories/${categoryId}`,
        { service_ids: newServices },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(categories.map(c => 
        c.id === categoryId ? { ...c, service_ids: newServices } : c
      ));
    } catch (error) {
      toast.error('Aktualisieren fehlgeschlagen');
    }
  };

  const getServiceById = (serviceId) => services.find(s => s.id === serviceId);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate('/portal-settings')}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
      </Button>
      
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Layers className="h-6 w-6 text-blue-500" /> Service-Kategorien
        </h1>
        <div className="flex gap-2">
          {/* Export Button */}
          <Button 
            variant="outline" 
            onClick={() => {
              const exportData = {
                export_version: "1.0",
                exported_at: new Date().toISOString(),
                exported_from: window.location.hostname,
                categories: categories
              };
              const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `service_kategorien_${new Date().toISOString().split('T')[0]}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              toast.success(`${categories.length} Kategorien exportiert!`);
            }}
            className="border-green-300 text-green-700 hover:bg-green-50"
            disabled={categories.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          
          {/* Import Button */}
          <input
            type="file"
            ref={importRef}
            accept=".json"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const text = await file.text();
                const data = JSON.parse(text);
                if (!data.categories || !Array.isArray(data.categories)) {
                  throw new Error('Ungültiges Dateiformat');
                }
                
                if (!window.confirm(
                  `${data.categories.length} Kategorien importieren?\n\n` +
                  `Von: ${data.exported_from || 'unbekannt'}\n` +
                  `Datum: ${data.exported_at ? new Date(data.exported_at).toLocaleString('de-DE') : 'unbekannt'}\n\n` +
                  `Bestehende Kategorien werden überschrieben!`
                )) {
                  e.target.value = '';
                  return;
                }
                
                // Save imported categories
                const token = localStorage.getItem('token');
                for (const cat of data.categories) {
                  await axios.post(
                    `${API}/portal/settings/categories`,
                    { name: cat.name, color: cat.color || '#3B82F6' },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                }
                
                toast.success(`${data.categories.length} Kategorien importiert!`);
                loadData(); // Reload categories
              } catch (error) {
                console.error('Import error:', error);
                toast.error('Import fehlgeschlagen: ' + error.message);
              } finally {
                e.target.value = '';
              }
            }}
          />
          <Button 
            variant="outline"
            onClick={() => importRef.current?.click()}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* New Category */}
        <Card>
          <CardHeader>
            <CardTitle>Neue Kategorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Kategoriename eingeben..."
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
              />
              <Button onClick={handleCreateCategory} disabled={saving || !newCategoryName.trim()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                <span className="ml-2">Hinzufügen</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Categories List */}
        {categories.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <p>Noch keine Kategorien erstellt.</p>
              <p className="text-sm mt-2">Erstellen Sie oben eine neue Kategorie oder importieren Sie Kategorien vom Live-System.</p>
            </CardContent>
          </Card>
        ) : (
          categories.map((category, index) => (
            <Card key={category.id} className="relative">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3 flex-1">
                  {/* Position Badge */}
                  <Badge variant="secondary" className="w-8 h-8 flex items-center justify-center rounded-full">
                    {index + 1}
                  </Badge>
                  
                  {/* Category Name - Edit Mode */}
                  {editingCategory === category.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="max-w-xs"
                        autoFocus
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(category.id)}
                      />
                      <Button size="icon" variant="ghost" onClick={() => handleSaveEdit(category.id)} className="text-green-600 hover:text-green-700">
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-600">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      <Button size="icon" variant="ghost" onClick={() => handleStartEdit(category)} className="h-7 w-7">
                        <Pencil className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                      </Button>
                    </div>
                  )}
                  
                  <Badge variant="outline">{(category.service_ids || []).length} Services</Badge>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-1">
                  {/* Move Up */}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleMoveCategory(category.id, 'up')}
                    disabled={index === 0}
                    className="h-8 w-8"
                    title="Nach oben"
                  >
                    <ChevronUp className={`h-5 w-5 ${index === 0 ? 'text-gray-300' : 'text-blue-500 hover:text-blue-600'}`} />
                  </Button>
                  
                  {/* Move Down */}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleMoveCategory(category.id, 'down')}
                    disabled={index === categories.length - 1}
                    className="h-8 w-8"
                    title="Nach unten"
                  >
                    <ChevronDown className={`h-5 w-5 ${index === categories.length - 1 ? 'text-gray-300' : 'text-blue-500 hover:text-blue-600'}`} />
                  </Button>
                  
                  {/* Delete */}
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteCategory(category.id)} className="h-8 w-8">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Label className="text-sm text-gray-500 mb-2 block">Services zuordnen:</Label>
                <div className="flex flex-wrap gap-2">
                  {services.filter(s => s.is_active && s.is_online_bookable).map(service => {
                    const isAssigned = (category.service_ids || []).includes(service.id);
                    return (
                      <Badge
                        key={service.id}
                        variant={isAssigned ? "default" : "outline"}
                        className={`cursor-pointer transition-all ${isAssigned ? 'bg-blue-500 hover:bg-blue-600' : 'hover:bg-gray-100'}`}
                        onClick={() => handleToggleService(category.id, service.id)}
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {service.name}
                      </Badge>
                    );
                  })}
                </div>
                {services.filter(s => s.is_active && s.is_online_bookable).length === 0 && (
                  <p className="text-sm text-gray-400">Keine online buchbaren Services verfügbar</p>
                )}
              </CardContent>
            </Card>
          ))
        )}

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => navigate('/portal-settings')}>
            Zurück zur Übersicht
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortalCategoriesSettings;
